import React from 'react';
import PropTypes from 'prop-types';

import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';

import MONTHS from '../../../shared/months.js';
import FormatDollarAmount from '../FormatDollarAmount.jsx';
import FormatPercentageValue from '../FormatPercentageValue.jsx';
import GrossTieredCell from './GrossTieredCell.jsx';

function CommGrossMonthlyCommissionsRow({ monthCommission }) {
  const isTotal = monthCommission.category === 'Total';
  const tier1Rate = Number(monthCommission.tier1 || 0);
  const tier2Rate = Number(monthCommission.tier2 || 0);
  const tier2Start = Number(monthCommission.tier2Start || 0);
  const tier3Start = Number(monthCommission.tier3Start || 0);

  // Convert the annual gross thresholds into cumulative commission-dollar
  // thresholds so monthly commission cells can still use the familiar tier colors.
  const commissionTier2Start = tier1Rate * tier2Start;
  const commissionTier3Start =
    commissionTier2Start + tier2Rate * Math.max(tier3Start - tier2Start, 0);
  const cumulativeMonthValues = MONTHS.reduce((running, month) => {
    const currentAmount = Number(monthCommission[month] || 0);
    const previousTotal = running[running.length - 1] || 0;
    running.push(previousTotal + currentAmount);
    return running;
  }, []);

  return (
    <TableRow>
      <TableCell>{monthCommission.category}</TableCell>
      <TableCell sx={(theme) => ({ backgroundColor: theme.palette.background.paper })}>
        {!isTotal && <FormatPercentageValue value={monthCommission.tier1} />}
      </TableCell>
      <TableCell
        sx={(theme) => ({
          backgroundColor: !isTotal ? 'aqua' : theme.palette.background.paper,
          color: !isTotal ? theme.palette.common.black : undefined,
        })}
      >
        {!isTotal && <FormatPercentageValue value={monthCommission.tier2} />}
      </TableCell>
      <TableCell
        sx={(theme) => ({
          backgroundColor: !isTotal ? 'aquamarine' : theme.palette.background.paper,
          color: !isTotal ? theme.palette.common.black : undefined,
        })}
      >
        {!isTotal && <FormatPercentageValue value={monthCommission.tier3} />}
      </TableCell>
      {MONTHS.map((month, index) => {
        const amount = Number(monthCommission[month] || 0);
        const cumulativeBasis = cumulativeMonthValues[index] || 0;
        return (
          <GrossTieredCell
            key={month}
            amount={amount}
            cumulativeBasis={cumulativeBasis}
            tier2Start={commissionTier2Start}
            tier3Start={commissionTier3Start}
            isTotal={isTotal}
            dataCy={`gross-monthly-commissions-${month}-${monthCommission.category}`}
          />
        );
      })}
      <TableCell>
        <FormatDollarAmount amount={monthCommission.total} />
      </TableCell>
    </TableRow>
  );
}

CommGrossMonthlyCommissionsRow.propTypes = {
  monthCommission: PropTypes.shape({
    category: PropTypes.string.isRequired,
    tier1: PropTypes.number,
    tier2: PropTypes.number,
    tier3: PropTypes.number,
    tier2Start: PropTypes.number,
    tier3Start: PropTypes.number,
    total: PropTypes.number.isRequired,
  }).isRequired,
};

export default React.memo(CommGrossMonthlyCommissionsRow);
